import React from 'react';
import PropTypes from 'prop-types';
import { FaCheck, FaCheckDouble } from 'react-icons/fa';

import Downshift from 'downshift';
import AutosizeInput from 'react-input-autosize';
import classNames from 'classnames';
import { defineMessages, injectIntl } from 'react-intl';
import { matchSorter } from 'match-sorter';
import intlShape from '@/shared/utils/intlShape';
import Button from '@/shared/components/Button';
import { filterBehaviourOptions } from '@/modules/constants';
import MultiSelectItem from './MultiSelectItem';
import Item from '../select/Item';
import adminpulse from '@/shared/utils/adminpulse';

export const itemEquals = (a, b, caseSensitive) => {
  if (caseSensitive) return a === b;
  return adminpulse.ciEquals(a, b);
};

const messages = defineMessages({
  NoItems: 'No items',
  OrFilter: 'OR filter',
  AndFilter: 'AND filter',
});
/**
 * Helper function to handle removing an item
 * @param {*} items The original selection
 * @param {*} item The item to remove
 * @param {*} [prop] The property to compare (default id)
 */
export const removeItem = (items, item, prop = 'id', caseSensitive = true) =>
  items.filter(x => !itemEquals(x, item.value[prop], caseSensitive));
/**
 * Helper function to handle addin an item
 * @param {*} items The original selection
 * @param {*} item The item to add
 * @param {*} [prop] The property to compare (default id)
 */
export const addItem = (items, item, prop = 'id', caseSensitive = true) => {
  if (!item) return [];

  return [...items.filter(x => !itemEquals(x, item[prop], caseSensitive)), item[prop]];
};
export const splitItems = (itemsArray, selectedArray, prop = 'id', caseSensitive = true) => {
  if (!itemsArray || !itemsArray.length) return [[], []]; // No items, return 2 empty arrays
  if (!selectedArray || !selectedArray.length) return [[], itemsArray]; // Nothing selected
  if (itemsArray.length === selectedArray.length) return [itemsArray, []]; // Everything selected
  // Mixed selection, splitting itemsArray into 2 arrays
  return itemsArray.reduce(
    (result, l) => {
      result[selectedArray.findIndex(x => itemEquals(x, l[prop], caseSensitive)) >= 0 ? 0 : 1].push(
        l,
      );
      return result;
    },
    [[], []],
  );
};

class MultiSelect extends React.Component {
  constructor(props) {
    super(props);
    const { items, filterBehaviour } = props;
    this.state = {
      inputValue: '',
      availableItems: items || [],
      filteredItems: items || [],
      filterBehaviour: filterBehaviour || filterBehaviourOptions.and,
    };
  }

  // Needed because the list of items might not be initialized on mount (because of translations etc)
  componentDidUpdate(prevProps) {
    const { items, filterBehaviour } = this.props;
    if (prevProps.items !== items) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        availableItems: items || [],
        filteredItems: items || [],
      });
    }

    if (prevProps.filterBehaviour !== filterBehaviour) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        filterBehaviour,
      });
    }
  }
  handleInputValueChange = inputValue => {
    const filteredItems = this.filterItems(inputValue);
    this.setState({ filteredItems });
  };

  filterItems = value => {
    const { searchProps } = this.props;
    const { availableItems } = this.state;

    if (!value) {
      return availableItems;
    }

    const filteredItems = matchSorter(availableItems, value, {
      keys: searchProps ?? ['label'],
      threshold: matchSorter.rankings.ACRONYM,
    });
    return filteredItems;
  };

  onInputChange = event => {
    this.setState({ inputValue: event.target.value });
  };

  onInputKeyDown = (event, highlightedIndex) => {
    const currentValue = event.target.value;
    const { inputValue, filteredItems } = this.state;
    const { onCreateItem, items } = this.props;

    switch (event.keyCode) {
      case 8: // backspace
        if (!currentValue) {
          event.preventDefault();
          this.popValue();
        }
        break;
      case 46: // delete
        if (!inputValue) {
          event.preventDefault();
          this.popValue();
        }
        break;
      case 13: {
        const trimmed = (inputValue || '').trim();
        if (!!trimmed && typeof onCreateItem === 'function') {
          const isItemHighlighted =
            highlightedIndex !== null &&
            highlightedIndex >= 0 &&
            filteredItems.length > 0 &&
            highlightedIndex < filteredItems.length;

          if (isItemHighlighted) break;
          // Item selected, let other function handle add
          else {
            const matchingItem = items.find(x => x.label.toLowerCase() === trimmed.toLowerCase());
            if (matchingItem) {
              // Nothing selected and full match on name, add matched item
              this.onChange(matchingItem);
              break;
            }
          }

          onCreateItem(trimmed);
          // Reset filtered items back to all items if one was selected
          this.setState(() => ({
            inputValue: '',
          }));
          event.preventDefault();
        }
        break;
      }
      default:
        break;
    }
  };

  popValue() {
    const { selectedItem, onItemRemoved } = this.props;
    if (onItemRemoved) {
      onItemRemoved({
        value: selectedItem[selectedItem.length - 1],
        index: selectedItem.length - 1,
      });
    }
  }

  onWrapperClick = e => {
    this.focusOnInput();
    e.stopPropagation();
    e.preventDefault();
  };

  focusOnInput() {
    if (!this.input) return;
    this.input.focus();
    if (typeof this.input.getInput === 'function') {
      this.input.getInput().focus();
    }
  }

  onChange = item => {
    const { onItemSelected } = this.props;
    const { availableItems } = this.state;

    // Reset filtered items back to all items if one was selected
    this.setState({ filteredItems: availableItems, inputValue: '' });
    onItemSelected(item);
  };

  onFilterBehaviourChanged = () => {
    const { onFilterBehaviourChanged, selectedItem } = this.props;
    const { filterBehaviour } = this.state;

    const newFilterBehaviour =
      filterBehaviour === filterBehaviourOptions.and
        ? filterBehaviourOptions.or
        : filterBehaviourOptions.and;

    this.setState({ filterBehaviour: newFilterBehaviour }, () => {
      // only pass to parent component if any items are selected because the filter change will trigger an API call
      if (selectedItem?.length > 0) onFilterBehaviourChanged(newFilterBehaviour);
    });
  };

  handleBlur = closeMenu => {
    this.setState({ inputValue: '' });
    closeMenu();
  };

  render() {
    const {
      disabled,
      placeholder,
      onItemRemoved,
      onCreateItem,
      onItemSelected,
      itemClassNameFunc,
      intl,
      isSmall,
      withFilterBehaviour,
      renderItem,
      ...rest
    } = this.props;

    const { filteredItems, inputValue, filterBehaviour } = this.state;
    return (
      <Downshift
        onInputValueChange={this.handleInputValueChange}
        defaultHighlightedIndex={0}
        itemToString={item => (item ? item.label : '')}
        onChange={this.onChange}
        {...rest}
      >
        {({
          getInputProps,
          getItemProps,
          isOpen,
          selectedItem,
          highlightedIndex,
          getToggleButtonProps,
          getMenuProps,
          closeMenu,
        }) => {
          const inputProps = getInputProps({
            value: inputValue,
            ref: inputRef => {
              this.input = inputRef;
            },
            inputClassName: 'multiselect-input',
            onChange: this.onInputChange,
            onKeyDown: e => this.onInputKeyDown(e, highlightedIndex),
            onBlur: () => this.handleBlur(closeMenu),
            disabled,
          });
          const selectedItems = selectedItem.map((item, index) => ({ value: item, index }));
          const hasItems = selectedItems.length > 0;

          return (
            <div className="multiselect-combobox">
              <div className={classNames({ 'input-group': true }, { 'input-group-sm': isSmall })}>
                <div className="multiselect-wrapper" onClick={this.onWrapperClick} tabIndex="-1">
                  {selectedItems.map(item => (
                    <MultiSelectItem
                      key={`Tag-${item.index}`}
                      onRemove={() => onItemRemoved(item)}
                      name={item.value.label}
                      className={itemClassNameFunc ? itemClassNameFunc(item.value) : undefined}
                    />
                  ))}
                  <AutosizeInput
                    {...inputProps}
                    placeholder={hasItems ? '' : placeholder}
                    inputStyle={hasItems ? {} : { width: '100%' }}
                    style={{ flex: 1 }}
                  />
                </div>
                <div className="input-group-btn">
                  <Button
                    id="dropdownButton"
                    btnClassName="Select--btn"
                    disabled={disabled}
                    {...getToggleButtonProps()}
                  >
                    <span className="caret" />
                  </Button>
                </div>
              </div>
              <div className={classNames({ open: isOpen }, { 'Select--relative': true })}>
                <ul
                  {...getMenuProps()}
                  className="Select--dropdown dropdown-menu dropdown-menu-right width100pct"
                >
                  {isOpen && withFilterBehaviour && (
                    <>
                      <li className="Select--item" onClick={this.onFilterBehaviourChanged}>
                        <a>
                          <span style={{ verticalAlign: 'middle' }}>
                            {filterBehaviour === filterBehaviourOptions.and && (
                              <FaCheckDouble className="margin5R" />
                            )}
                            {filterBehaviour === filterBehaviourOptions.or && (
                              <FaCheck className="margin5R margin5T" />
                            )}
                          </span>

                          {intl.formatMessage(
                            filterBehaviour === filterBehaviourOptions.or
                              ? messages.OrFilter
                              : messages.AndFilter,
                          )}
                        </a>
                      </li>

                      <li role="separator" className="divider" />
                    </>
                  )}
                  {isOpen &&
                    filteredItems.map((item, index) => (
                      <Item
                        key={item.id}
                        item={item}
                        index={index}
                        isSelected={highlightedIndex === index}
                        isActive={selectedItem === item}
                        getItemProps={getItemProps}
                        disabled={item.disabled}
                        renderItem={renderItem}
                        searchValue={inputValue}
                        className={itemClassNameFunc ? itemClassNameFunc(item) : undefined}
                      />
                    ))}

                  {isOpen && (
                    <Item item={null} getItemProps={getItemProps}>
                      {(!filteredItems || filteredItems.length < 1) &&
                        intl.formatMessage(messages.NoItems)}
                    </Item>
                  )}
                </ul>
              </div>
            </div>
          );
        }}
      </Downshift>
    );
  }
}

MultiSelect.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any),
  selectedItem: PropTypes.arrayOf(PropTypes.any),
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  isSmall: PropTypes.bool,
  onItemRemoved: PropTypes.func.isRequired,
  onItemSelected: PropTypes.func.isRequired,
  onCreateItem: PropTypes.func, // If function exists, passes inputValue on enter -> (inputValue)=>{ should add value to items and select item }

  /** Specify custom classname for items */
  itemClassNameFunc: PropTypes.func /* (item)=>classname string or object */,
  intl: intlShape.isRequired,
  withFilterBehaviour: PropTypes.bool,
  filterBehaviour: PropTypes.number,
  searchProps: PropTypes.arrayOf(PropTypes.string),
};
MultiSelect.defaultProps = {
  disabled: false,
  placeholder: null,
  isSmall: false,
  items: [],
  selectedItem: null,
  onCreateItem: undefined,
  itemClassNameFunc: undefined,
  withFilterBehaviour: false,
  filterBehaviour: 1,
  searchProps: ['label'],
};

export default injectIntl(MultiSelect);
